import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useLang } from '../utils/LanguageContext';
import { t } from '../utils/translations';
import './CarnetScreen.css';
import Loader from '../components/Loader';

// Carnet — regulars who order now and pay later.
function CarnetScreen() {
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [openId, setOpenId] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [saving, setSaving] = useState(false);

  const { lang } = useLang();
  const tr = t[lang];

  const fetchAll = useCallback(async () => {
    const [{ data: cust }, { data: ords }, { data: pays }] = await Promise.all([
      supabase.from('customers').select('*').order('created_at', { ascending: false }),
      supabase.from('orders').select('id, total, customer_id, created_at').eq('payment_method', 'carnet'),
      supabase.from('carnet_payments').select('*').order('created_at', { ascending: false }),
    ]);
    setCustomers(cust || []);
    setOrders(ords || []);
    setPayments(pays || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  function balanceOf(id) {
    const owed = orders.filter(o => o.customer_id === id).reduce((s, o) => s + parseFloat(o.total), 0);
    const paid = payments.filter(p => p.customer_id === id).reduce((s, p) => s + parseFloat(p.amount), 0);
    return owed - paid;
  }

  async function addCustomer(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true);
    await supabase.from('customers').insert([{ name: newName.trim(), phone: newPhone.trim() || null }]);
    setNewName(''); setNewPhone(''); setShowAdd(false);
    await fetchAll();
    setSaving(false);
  }

  async function recordPayment(customerId) {
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) return;
    setSaving(true);
    await supabase.from('carnet_payments').insert([{ customer_id: customerId, amount: amt }]);
    setPayAmount('');
    await fetchAll();
    setSaving(false);
  }

  if (loading) return <Loader text={tr.loadingApp} />;

  const totalOwed = customers.reduce((s, c) => s + Math.max(0, balanceOf(c.id)), 0);

  return (
    <div className="carnet">
      <div className="carnet-header">
        <h2 className="carnet-title">📒 {tr.carnet}</h2>
        <button className="carnet-add-btn" onClick={() => setShowAdd(true)}>+ {tr.addCustomer}</button>
      </div>

      <div className="carnet-summary">
        <span>{tr.totalOwed}</span>
        <strong>{totalOwed.toFixed(0)} {tr.currency}</strong>
      </div>

      {customers.length === 0 && <p className="carnet-empty">{tr.noCustomers}</p>}

      {customers.map(c => {
        const bal = balanceOf(c.id);
        const open = openId === c.id;
        return (
          <div key={c.id} className="carnet-card">
            <div className="carnet-row" onClick={() => setOpenId(open ? null : c.id)}>
              <div className="carnet-info">
                <span className="carnet-name">{c.name}</span>
                {c.phone && <span className="carnet-phone">{c.phone}</span>}
              </div>
              <span className={`carnet-balance ${bal > 0 ? 'owes' : 'clear'}`}>
                {bal > 0 ? `${bal.toFixed(0)} ${tr.currency}` : `✓ ${tr.settled}`}
              </span>
            </div>

            {open && (
              <div className="carnet-detail">
                {bal > 0 && (
                  <div className="carnet-pay">
                    <input
                      type="number"
                      inputMode="numeric"
                      placeholder={tr.paymentAmount}
                      value={payAmount}
                      onChange={e => setPayAmount(e.target.value)}
                    />
                    <button onClick={() => recordPayment(c.id)} disabled={saving}>
                      {tr.recordPayment}
                    </button>
                    <button className="pay-all" onClick={() => { setPayAmount(String(bal)); }}>
                      {bal.toFixed(0)}
                    </button>
                  </div>
                )}
                <div className="carnet-mini">
                  {orders.filter(o => o.customer_id === c.id).length} {tr.carnetOrders}
                  {' · '}
                  {payments.filter(p => p.customer_id === c.id).length} {tr.carnetPayments}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <form className="modal carnet-modal" onClick={e => e.stopPropagation()} onSubmit={addCustomer}>
            <h3>{tr.addCustomer}</h3>
            <input
              type="text"
              placeholder={tr.customerName}
              value={newName}
              onChange={e => setNewName(e.target.value)}
              autoFocus
              required
            />
            <input
              type="tel"
              placeholder={tr.customerPhone}
              value={newPhone}
              onChange={e => setNewPhone(e.target.value)}
            />
            <div className="modal-btns">
              <button type="button" className="cancel-btn" onClick={() => setShowAdd(false)}>{tr.cancel}</button>
              <button type="submit" className="save-btn" disabled={saving}>{tr.save}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default CarnetScreen;
